<script lang="ts">
import RichText from '$components/Card/RichText.svelte';
import { pickExplainCard, sokratesPrompt } from '$lib/fachgespraech';
import type { Card } from '$types/card';

interface Props {
  cards: readonly Card[];
}

const { cards }: Props = $props();

let card = $state<Card | null>(null);
let showSolution = $state(false);
let checked = $state<boolean[]>([]);
let copyStatus = $state<string | null>(null);
let recState = $state<'idle' | 'recording' | 'done' | 'unsupported'>('idle');
let recError = $state<string | null>(null);
let audioUrl = $state<string | null>(null);
let recorder: MediaRecorder | null = null;
let chunks: Blob[] = [];

const rubric = $derived(card?.self_explain?.rubric ?? []);
const prompt = $derived(
  card?.self_explain?.prompt ??
    (card && 'front' in card ? `Erkläre laut und frei (≈ 60 s): ${card.front ?? ''}` : ''),
);

function draw(): void {
  cleanupAudio();
  card = pickExplainCard(cards);
  showSolution = false;
  checked = [];
  copyStatus = null;
  recState = 'idle';
  recError = null;
}

function stopTracks(): void {
  for (const t of recorder?.stream.getTracks() ?? []) t.stop();
}

function cleanupAudio(): void {
  if (audioUrl) URL.revokeObjectURL(audioUrl);
  audioUrl = null;
  stopTracks();
  recorder = null;
  chunks = [];
}

async function startRecording(): Promise<void> {
  recError = null;
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
    recState = 'unsupported';
    return;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    chunks = [];
    recorder = new MediaRecorder(stream);
    recorder.ondataavailable = (e) => chunks.push(e.data);
    recorder.onstop = () => {
      audioUrl = URL.createObjectURL(
        new Blob(chunks, { type: recorder?.mimeType ?? 'audio/webm' }),
      );
      recState = 'done';
    };
    recorder.start();
    recState = 'recording';
  } catch (e) {
    recError = e instanceof Error ? e.message : String(e);
    recState = 'idle';
  }
}

function stopRecording(): void {
  recorder?.stop();
  stopTracks();
}

async function copyPrompt(): Promise<void> {
  if (!card) return;
  try {
    await navigator.clipboard.writeText(sokratesPrompt(card));
    copyStatus = '✓ prompt kopiert — in ChatGPT/Claude Voice einfügen';
  } catch {
    copyStatus = '✗ zwischenablage nicht verfügbar';
  }
}

const rubricScore = $derived(checked.filter(Boolean).length);
</script>

<section class="fg">
  <div class="rule"><span class="rule-label">[ FACHGESPRÄCH-DRILL ]</span><span class="rule-line"></span></div>
  <p class="hint">
    Wochenroutine für DEN Hebel zur Note 1: laut erklären wie im Fachgespräch (~20 min in der
    praktischen Prüfung), aufnehmen, gegen die Rubric selbst auditieren.
  </p>

  {#if !card}
    <button type="button" class="btn-start" onclick={draw} disabled={cards.length === 0}>▶ thema ziehen</button>
  {:else}
    <div class="fg-card">
      <div class="fg-meta">{card.deck}{#if card.source}· {card.source}{/if}</div>
      <div class="fg-prompt"><RichText content={prompt} /></div>

      <div class="fg-rec">
        {#if recState === 'idle'}
          <button type="button" class="btn-start" onclick={startRecording}>● aufnahme starten</button>
          {#if recError}<span class="err">✗ mikrofon: {recError}</span>{/if}
        {:else if recState === 'recording'}
          <button type="button" class="btn-rec" onclick={stopRecording}>■ aufnahme stoppen</button>
          <span class="rec-live">● läuft …</span>
        {:else if recState === 'done' && audioUrl}
          <audio controls src={audioUrl}></audio>
          <button type="button" class="mini" onclick={startRecording}>↺ neu aufnehmen</button>
        {:else if recState === 'unsupported'}
          <span class="err">Aufnahme wird von diesem Browser nicht unterstützt — Sprachmemo-App nutzen.</span>
        {/if}
      </div>

      {#if rubric.length}
        <div class="fg-rubric">
          <div class="rubric-label">selbst-audit: kam das vor?</div>
          {#each rubric as item, i}
            <label class="rubric-item">
              <input
                type="checkbox"
                checked={checked[i] ?? false}
                onchange={(e) => {
                  const next = [...checked];
                  next[i] = (e.currentTarget as HTMLInputElement).checked;
                  checked = next;
                }} />
              {item}
            </label>
          {/each}
          <div class="rubric-score" class:good={rubricScore === rubric.length}>
            {rubricScore}/{rubric.length} Rubric-Punkte
          </div>
        </div>
      {/if}

      <div class="fg-actions">
        <button type="button" class="mini" onclick={() => (showSolution = !showSolution)}>
          {showSolution ? 'musterlösung verstecken' : 'musterlösung zeigen'}
        </button>
        <button type="button" class="mini" onclick={copyPrompt}>⎘ sokrates-prompt kopieren</button>
        <button type="button" class="mini" onclick={draw}>nächstes thema →</button>
      </div>
      {#if copyStatus}<div class="copy-status">{copyStatus}</div>{/if}

      {#if showSolution && 'back' in card && card.back}
        <div class="fg-solution">
          <div class="rubric-label">↳ musterlösung</div>
          <RichText content={card.back} />
        </div>
      {/if}
    </div>
  {/if}
</section>

<style>
  .fg { margin-top: 8px; }
  .rule {
    display: flex; align-items: center; gap: 10px;
    margin: 16px 0 8px;
    font-size: 11px; letter-spacing: 0.08em;
    color: var(--ink-3); text-transform: uppercase;
  }
  .rule-label { white-space: nowrap; font-weight: 600; }
  .rule-line { flex: 1; height: 0; border-top: 1px dashed var(--rule); opacity: 0.5; }
  .hint { font-size: 13px; color: var(--ink-2); }

  .btn-start {
    border: 2px solid var(--ink); background: var(--ink); color: var(--paper);
    padding: 10px 18px; font-family: var(--font-mono); font-size: 12px;
    font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; cursor: pointer;
  }
  .btn-start:hover { background: var(--accent); border-color: var(--accent); }
  .btn-start:disabled { opacity: 0.4; cursor: not-allowed; }

  .fg-card {
    border: 2px solid var(--ink); background: var(--paper);
    padding: 18px; box-shadow: 6px 6px 0 var(--ink-3); margin-top: 10px;
  }
  .fg-meta { font-size: 10px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 8px; }
  .fg-prompt { font-size: 18px; font-weight: 500; }

  .fg-rec { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 14px; }
  .btn-rec {
    border: 2px solid var(--r-again, #c62828); background: var(--r-again, #c62828); color: #fff;
    padding: 10px 18px; font-family: var(--font-mono); font-size: 12px;
    font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; cursor: pointer;
  }
  .rec-live { color: var(--r-again, #c62828); font-weight: 700; animation: blink 1s steps(2) infinite; }
  @keyframes blink { 50% { opacity: 0.2; } }
  .err { color: var(--r-again, #c62828); font-size: 12px; }
  audio { max-width: 100%; }

  .fg-rubric { margin-top: 14px; padding: 10px; background: var(--paper-2); border-left: 4px solid var(--accent); }
  .rubric-label {
    font-size: 10px; letter-spacing: 0.12em; color: var(--accent-2);
    text-transform: uppercase; font-weight: 700; margin-bottom: 6px;
  }
  .rubric-item { display: flex; gap: 8px; align-items: center; padding: 3px 0; font-size: 14px; cursor: pointer; }
  .rubric-score { margin-top: 8px; font-weight: 700; font-size: 12px; text-transform: uppercase; }
  .rubric-score.good { color: var(--r-good, #2e7d32); }

  .fg-actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 14px; }
  .mini {
    background: transparent; border: 1px solid var(--rule); color: var(--ink-2);
    padding: 6px 10px; font-family: var(--font-mono); font-size: 10px;
    text-transform: uppercase; letter-spacing: 0.06em; cursor: pointer;
  }
  .mini:hover { background: var(--paper-2); }
  .copy-status { margin-top: 8px; font-size: 11px; color: var(--muted); }
  .fg-solution { margin-top: 12px; padding: 10px; background: var(--paper-2); border-left: 4px solid var(--accent); font-size: 14px; }
</style>
